// The canonical `thuto-expression-v1` value (Senior Phase primitives contract
// §2.4): a bounded grammar of integer/decimal literals, declared symbols,
// + - * / ^ =, parentheses and sqrt(...).
//
// This is deliberately not a computer algebra system. Normalisation is
// syntax-only: whitespace, Unicode operator aliases, numeric literals,
// implicit multiplication and redundant parentheses. Two expressions match
// only when their parsed structure is the same, so `5x` and `5*x` match, but
// `x*5`, `2x+3x` or `5(x)`-style rewrites of different structure do not
// unless they are authored as accepted forms.

export const EXPRESSION_FORMAT = "thuto-expression-v1";
export const OPERATORS = ["+", "-", "*", "/", "^", "=", "sqrt", "(", ")"];

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const isExpressionValue = (value) =>
  isObject(value) &&
  value.format === EXPRESSION_FORMAT &&
  typeof value.source === "string" &&
  value.source.trim() !== "";

// Unicode the keypad, a pasted answer or a hardware keyboard may produce.
const ALIASES = [
  [/[×⋅·∙]/g, "*"],
  [/[÷∕]/g, "/"],
  [/[−–—]/g, "-"],
  [/√/g, "sqrt"],
  [/²/g, "^2"],
  [/³/g, "^3"],
];

export class ExpressionError extends Error {
  constructor(reason) {
    super(reason);
    this.reason = reason;
  }
}

// Tokens: numbers, declared symbols (longest declared name first, so "ab"
// beats "a" when both are declared), sqrt, operators and parentheses.
const tokenize = (source, symbols) => {
  let text = source;
  for (const [pattern, replacement] of ALIASES) text = text.replace(pattern, replacement);
  text = text.replace(/\s+/g, "");
  if (!text) throw new ExpressionError("empty");
  const names = [...symbols].sort((a, b) => b.length - a.length);
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    const rest = text.slice(i);
    const number = /^(\d+(\.\d+)?|\.\d+)/.exec(rest);
    if (number) {
      tokens.push({ type: "num", value: number[0] });
      i += number[0].length;
      continue;
    }
    if (rest.startsWith("sqrt")) {
      tokens.push({ type: "sqrt" });
      i += 4;
      continue;
    }
    if ("+-*/^=()".includes(rest[0])) {
      tokens.push({ type: "op", value: rest[0] });
      i += 1;
      continue;
    }
    const name = names.find((n) => rest.startsWith(n));
    if (name) {
      tokens.push({ type: "sym", value: name });
      i += name.length;
      continue;
    }
    throw new ExpressionError(/[a-z]/i.test(rest[0]) ? "symbol" : "character");
  }
  return tokens;
};

const canonicalNumber = (text) => {
  let [whole, fraction] = (text.startsWith(".") ? `0${text}` : text).split(".");
  whole = whole.replace(/^0+(?=\d)/, "");
  fraction = (fraction || "").replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole;
};

// Recursive descent with the usual precedence:
//   equation := sum ("=" sum)?
//   sum      := term (("+" | "-") term)*
//   term     := unary (("*" | "/") unary | <implicit> power)*
//   unary    := ("-" | "+") unary | power
//   power    := primary ("^" unary)?
//   primary  := number | symbol | "(" sum ")" | sqrt "(" sum ")"
// Implicit multiplication (5x, 2(x+1), x sqrt(4)) becomes "*". A unary plus
// is dropped from the tree; a unary minus stays a node of its own.
//
// `used` records every operator the expression uses, as its operator key in
// `answer.operators`, at the point the parser consumes it: unary plus is "+"
// and unary minus "-" (the grammar has one plus and one minus sign), implicit
// multiplication is "*", grouping brackets are "(" and ")", sqrt is "sqrt"
// (its own brackets belong to it) and an equation is "=". Recording here,
// rather than walking the tree, means nothing the tree drops can escape.
const parseTokens = (tokens, { allowEquation }) => {
  let i = 0;
  const used = new Set();
  const peek = () => tokens[i];
  const isOp = (value) => peek()?.type === "op" && peek().value === value;
  const expect = (value) => {
    if (!isOp(value)) throw new ExpressionError("malformed");
    i += 1;
  };

  const startsPrimary = () => {
    const t = peek();
    return t && (t.type === "num" || t.type === "sym" || t.type === "sqrt" || (t.type === "op" && t.value === "("));
  };

  const primary = () => {
    const t = peek();
    if (!t) throw new ExpressionError("incomplete");
    if (t.type === "num") {
      i += 1;
      return { type: "num", value: canonicalNumber(t.value) };
    }
    if (t.type === "sym") {
      i += 1;
      return { type: "sym", name: t.value };
    }
    if (t.type === "sqrt") {
      i += 1;
      expect("(");
      const inner = sum();
      expect(")");
      used.add("sqrt");
      return { type: "sqrt", arg: inner };
    }
    if (isOp("(")) {
      i += 1;
      const inner = sum();
      expect(")");
      used.add("(").add(")");
      return inner;
    }
    throw new ExpressionError("malformed");
  };

  const power = () => {
    const base = primary();
    if (isOp("^")) {
      i += 1;
      used.add("^");
      return { type: "pow", left: base, right: unary() };
    }
    return base;
  };

  const unary = () => {
    if (isOp("-")) {
      i += 1;
      used.add("-");
      return { type: "neg", arg: unary() };
    }
    if (isOp("+")) {
      i += 1;
      used.add("+");
      return unary();
    }
    return power();
  };

  const term = () => {
    let left = unary();
    for (;;) {
      if (isOp("*") || isOp("/")) {
        const op = peek().value;
        i += 1;
        used.add(op);
        left = { type: op === "*" ? "mul" : "div", left, right: unary() };
      } else if (startsPrimary()) {
        used.add("*");
        left = { type: "mul", left, right: power() };
      } else {
        return left;
      }
    }
  };

  const sum = () => {
    let left = term();
    while (isOp("+") || isOp("-")) {
      const op = peek().value;
      i += 1;
      used.add(op);
      left = { type: op === "+" ? "add" : "sub", left, right: term() };
    }
    return left;
  };

  const left = sum();
  let tree = left;
  if (isOp("=")) {
    if (!allowEquation) throw new ExpressionError("equation");
    i += 1;
    used.add("=");
    tree = { type: "eq", left, right: sum() };
  }
  if (i !== tokens.length) {
    throw new ExpressionError("malformed");
  }
  return { tree, used };
};

// The one parse every expression in a correctness decision goes through:
// the learner's entry, every authored accepted form, and both sides of a
// comparison. `contract` is { symbols, operators, allowEquation }.
//
// With `operators` given (always, wherever correctness is decided), every
// operator the parser recorded must be declared, with no exceptions: unary
// plus needs "+", unary minus "-", implicit multiplication (5x) "*", grouping
// brackets both "(" and ")", sqrt "sqrt", and an equation "=". An equation
// must also be allowed by `allowEquation` (contract §4.3): "=" needs both.
// Throws ExpressionError.
export const parseExpression = (
  source,
  { symbols = [], operators = null, allowEquation = false } = {}
) => {
  const tokens = tokenize(String(source ?? ""), symbols);
  const { tree, used } = parseTokens(tokens, { allowEquation });
  if (operators) {
    for (const op of used) {
      if (!operators.includes(op)) throw new ExpressionError("operator");
    }
  }
  return tree;
};

// A canonical `thuto-expression-v1` value parsed under a contract. Anything
// else (another format or version, a non-string source) is refused before
// parsing. Throws ExpressionError.
export const parseExpressionValue = (value, contract) => {
  if (!isExpressionValue(value)) throw new ExpressionError("format");
  return parseExpression(value.source, contract);
};

// Every operator the bounded grammar has, except "=": what a context that
// declares no operators of its own (a table cell) may use.
export const BOUNDED_OPERATORS = OPERATORS.filter((op) => op !== "=");

// --- Canonical source and display -----------------------------------------

const PRECEDENCE = { eq: 0, add: 1, sub: 1, mul: 2, div: 2, neg: 3, pow: 4 };
const ATOM = 5;
const precedenceOf = (tree) => PRECEDENCE[tree.type] ?? ATOM;

// The canonical source: explicit "*", minimal parentheses for the same tree.
export const toSource = (tree) => {
  const wrap = (child, needed) => (needed ? `(${toSource(child)})` : toSource(child));
  switch (tree.type) {
    case "num":
      return tree.value;
    case "sym":
      return tree.name;
    case "sqrt":
      return `sqrt(${toSource(tree.arg)})`;
    case "neg":
      return `-${wrap(tree.arg, precedenceOf(tree.arg) < PRECEDENCE.pow)}`;
    case "pow":
      return `${wrap(tree.left, precedenceOf(tree.left) <= PRECEDENCE.pow)}^${wrap(
        tree.right,
        precedenceOf(tree.right) < ATOM
      )}`;
    case "eq":
      return `${toSource(tree.left)}=${toSource(tree.right)}`;
    default: {
      const op = { add: "+", sub: "-", mul: "*", div: "/" }[tree.type];
      const p = PRECEDENCE[tree.type];
      // Left-associative: the right side needs brackets at equal precedence.
      return `${wrap(tree.left, precedenceOf(tree.left) < p)}${op}${wrap(
        tree.right,
        precedenceOf(tree.right) <= p
      )}`;
    }
  }
};

const sameTree = (a, b) =>
  a.type === b.type &&
  a.value === b.value &&
  a.name === b.name &&
  ["left", "right", "arg"].every((key) =>
    a[key] || b[key] ? Boolean(a[key] && b[key]) && sameTree(a[key], b[key]) : true
  );

// The learner's canonical expression value, or { error } with a reason:
// "empty", "incomplete", "malformed", "symbol", "operator", "character" or
// "equation".
export const readExpressionEntry = (text, options) => {
  try {
    const tree = parseExpression(text, options);
    return { value: { format: EXPRESSION_FORMAT, source: toSource(tree) }, tree };
  } catch (error) {
    if (error instanceof ExpressionError) return { error: error.reason };
    throw error;
  }
};

// Whether the learner's expression is one of the explicitly accepted forms,
// after syntax-only normalisation. Both sides go through the same contract:
// an expression (or accepted form) that breaks it never matches, whatever it
// says. Structural equality only: never algebraic equivalence.
export const expressionMatches = (value, accepted, contract) => {
  let tree;
  try {
    tree = parseExpressionValue(value, contract);
  } catch {
    return false;
  }
  return accepted.some((form) => {
    try {
      return sameTree(tree, parseExpressionValue(form, contract));
    } catch {
      return false;
    }
  });
};

// Symbols named in an expression source, for a context that declares none
// (a table cell): every maximal run of letters that is not sqrt.
export const symbolsIn = (source) =>
  [...new Set((String(source).replace(/sqrt/g, " ").match(/[a-z]+/gi) || []))];

// Familiar notation for learners and teachers: implicit multiplication where
// it reads naturally (5x, 2(x+1)), × elsewhere, superscript powers, √.
const SUPERSCRIPT = { 0: "⁰", 1: "¹", 2: "²", 3: "³", 4: "⁴", 5: "⁵", 6: "⁶", 7: "⁷", 8: "⁸", 9: "⁹" };

export const toDisplay = (tree) => {
  const wrap = (child, needed) => (needed ? `(${toDisplay(child)})` : toDisplay(child));
  switch (tree.type) {
    case "num":
      return tree.value;
    case "sym":
      return tree.name;
    case "sqrt":
      return `√(${toDisplay(tree.arg)})`;
    case "neg":
      return `−${wrap(tree.arg, precedenceOf(tree.arg) < PRECEDENCE.pow)}`;
    case "pow": {
      const base = wrap(tree.left, precedenceOf(tree.left) <= PRECEDENCE.pow);
      if (tree.right.type === "num" && /^\d+$/.test(tree.right.value)) {
        return base + [...tree.right.value].map((d) => SUPERSCRIPT[d]).join("");
      }
      return `${base}^${wrap(tree.right, precedenceOf(tree.right) < ATOM)}`;
    }
    case "eq":
      return `${toDisplay(tree.left)} = ${toDisplay(tree.right)}`;
    case "mul": {
      const left = wrap(tree.left, precedenceOf(tree.left) < PRECEDENCE.mul);
      const right = wrap(tree.right, precedenceOf(tree.right) <= PRECEDENCE.mul);
      // 5x, 3√(x) and 2(x+1) read as multiplication; 2 × 3 and x × y keep
      // the sign so neither digits nor letters run together.
      const bracketed = right.startsWith("(");
      const letterOrRoot = /^[a-z\u221a]/i.test(right);
      const implicit = bracketed || (tree.left.type === "num" && letterOrRoot);
      return implicit ? `${left}${right}` : `${left} × ${right}`;
    }
    default: {
      const op = { add: " + ", sub: " − ", div: " ÷ " }[tree.type];
      const p = PRECEDENCE[tree.type];
      return `${wrap(tree.left, precedenceOf(tree.left) < p)}${op}${wrap(
        tree.right,
        precedenceOf(tree.right) <= p
      )}`;
    }
  }
};

// Display of a canonical value, or its raw source if it cannot be read.
export const formatExpression = (value, options = {}) => {
  if (!isExpressionValue(value)) return "";
  try {
    const symbols = options.symbols || symbolsIn(value.source);
    return toDisplay(parseExpression(value.source, { symbols, allowEquation: true }));
  } catch {
    return value.source;
  }
};
